import React, { type Dispatch, type SetStateAction } from 'react'
import { Form } from 'react-bootstrap'
import { isValidUrl } from '../../../common/urlValidation'
import { SHORT_DESCRIPTION_MAX } from '../constants'

export interface AppDetailsFormData {
  name: string
  imageUrl: string
  shortDescription: string
  about: string
  whatYouCanDo: string
}

interface AppDetailsFormFieldsProps {
  formData: AppDetailsFormData
  setFormData?: Dispatch<SetStateAction<AppDetailsFormData>>
  validated?: boolean
  readOnly?: boolean
}

export const AppDetailsFormFields: React.FC<AppDetailsFormFieldsProps> = ({ formData, setFormData, validated = false, readOnly = false }) => {
  if (readOnly) {
    return (
      <>
        <div className='mb-3'>
          <Form.Label className='fw-bold small text-muted'>Logo URL</Form.Label>
          <div>{formData.imageUrl || '—'}</div>
        </div>
        <div className='mb-3'>
          <Form.Label className='fw-bold small text-muted'>App name</Form.Label>
          <div>{formData.name}</div>
        </div>
        <div className='mb-3'>
          <Form.Label className='fw-bold small text-muted'>Short description</Form.Label>
          <div>{formData.shortDescription || '—'}</div>
        </div>
        <div className='mb-3'>
          <Form.Label className='fw-bold small text-muted'>About</Form.Label>
          <div className='white-space-pre-line'>{formData.about || '—'}</div>
        </div>
        <div className='mb-4'>
          <Form.Label className='fw-bold small text-muted'>What you can do</Form.Label>
          <div className='white-space-pre-line'>{formData.whatYouCanDo || '—'}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <Form.Group className='mb-3'>
        <Form.Label>Logo URL</Form.Label>
        <Form.Control
          type='url'
          placeholder='https://...'
          value={formData.imageUrl}
          onChange={(e) => setFormData?.((prev) => ({ ...prev, imageUrl: e.target.value }))}
          isInvalid={validated && formData.imageUrl.trim() !== '' && !isValidUrl(formData.imageUrl)}
        />
        <Form.Control.Feedback type='invalid'>Please enter a valid URL.</Form.Control.Feedback>
        <Form.Text className='text-muted'>256×256 px or larger. PNG or SVG, square ratio, max 512 KB</Form.Text>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label>
          App name <span className='text-danger'>*</span>
        </Form.Label>
        <Form.Control
          type='text'
          required
          value={formData.name}
          onChange={(e) => setFormData?.((prev) => ({ ...prev, name: e.target.value }))}
          isInvalid={validated && !formData.name.trim()}
        />
        <Form.Control.Feedback type='invalid'>App name is required.</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label>
          Short description <span className='text-danger'>*</span>
        </Form.Label>
        <Form.Control
          as='textarea'
          rows={2}
          required
          value={formData.shortDescription}
          onChange={(e) => setFormData?.((prev) => ({ ...prev, shortDescription: e.target.value }))}
          isInvalid={validated && (!formData.shortDescription.trim() || formData.shortDescription.length > SHORT_DESCRIPTION_MAX)}
          maxLength={SHORT_DESCRIPTION_MAX}
        />
        <Form.Text className='text-muted'>{SHORT_DESCRIPTION_MAX} characters max.</Form.Text>
        <Form.Control.Feedback type='invalid'>
          {!formData.shortDescription.trim() ? 'Short description is required.' : `Maximum ${SHORT_DESCRIPTION_MAX} characters.`}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className='mb-3'>
        <Form.Label>About</Form.Label>
        <Form.Control as='textarea' rows={4} value={formData.about} onChange={(e) => setFormData?.((prev) => ({ ...prev, about: e.target.value }))} />
        <Form.Text className='text-muted'>Shown on the About section of your app.</Form.Text>
      </Form.Group>

      <Form.Group className='mb-4'>
        <Form.Label>What you can do</Form.Label>
        <Form.Control
          as='textarea'
          rows={4}
          value={formData.whatYouCanDo}
          onChange={(e) => setFormData?.((prev) => ({ ...prev, whatYouCanDo: e.target.value }))}
        />
        <Form.Text className='text-muted'>Each line will appear as a bullet point on the About section.</Form.Text>
      </Form.Group>
    </>
  )
}
